import * as React from 'react';

import { getUserFilters } from '@store/actions/search';
import {
    ISecurity, IUserSearchFilter, UserSearchFilterType
} from '@models';
import {
    Button, InputSwitch, OverlayPanel, useLoading, useTranslation, MultiSelect, Select
} from '@components';
import {
    storageGet, storageSet
} from '@utils/storage';
import InputEventDispatcher from './InputEventDispatcher';
import { delay } from '@utils';
import './useSearchFilters.scss';

export interface IProps {
    workId: number;
    name: string;
    references?: any;
    onChange?: Function;
    security?: ISecurity;
    persist?: boolean;
    awaitForApply?: boolean;
    awaitForDoSearch?: boolean;
    getXml?: Function;
}

function FilterInputTextOnEnter(props: any) {
    const [value, setValue] = React.useState<string>(props.value || '');

    const onKeyPress = (e: any) => {
        if (e.key == 'Enter') {
            props.onChange(value);
        }
    }

    return <input
        type='text'
        value={value}
        onKeyPress={onKeyPress}
        onChange={e => setValue(e.target.value)} />
}

function FilterInputText(props: any) {
    const [value, setValue] = React.useState<string>(props.value || '');

    // const dispatcher = React.useRef(
    //     new InputEventDispatcher(
    //         props.onChange,
    //         setValue,
    //         500
    //     ));

    return <input
        type='text'
        value={value}
        onBlur={e => {
            props.onChange(value);
        }}
        onChange={e => {
            setValue(e.target.value);
            // dispatcher.current.append(e)
        }}
        />
}

export default function useSearchFilters(props: IProps) {
    const getLocalStorageKey = () => {
        return `filters_${props.name}_${props.workId}`;
    }

    const recoverStorage = () => {
        const data = storageGet(getLocalStorageKey(), {});
        return data;
    }

    const [filters, setFilters] = React.useState<IUserSearchFilter[]>([]);
    const [filterValues, setFilterValues] = React.useState<any>(
        recoverStorage()
    );


    const [temporaryFilterValues, setTemporaryFilterValues] = React.useState<any>([]);

    const [defaultFilterValues, setDefaultFilterValues] = React.useState<any>({});
    const [error, setError] = React.useState<string | undefined>();
    const [searchOrder, setSearchOrder] = React.useState<boolean>(false);

    const returnSearchOrderToFalse = () => {
        setSearchOrder(false)
    }

    const propagate = React.useRef<boolean>(false);
    const loading = useLoading();
    const mountedRef = React.useRef(true);
    const { t } = useTranslation();

    const buttonRef = React.useRef<any>(null);

    React.useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const canApplyFilter = (f: IUserSearchFilter) => {
        return (f.excludeRole && !props.security?.hasRole(f.excludeRole))
            || (!f.excludeRole && f.requireRole && props.security?.hasRole(f.requireRole))
            || (!f.excludeRole && !f.requireRole);
    }

    const initialize = loading.wrap(async () => {
        try {
            const storeKey = getLocalStorageKey();
            const state: any = props.persist
                ? storageGet<any>(storeKey, {})
                : {};
            const rawFs = await getUserFilters(props.workId, props.name);
            if (!mountedRef.current) return;
            const fs = rawFs.filter(canApplyFilter);
            const values: any = {};
            const defaultValues: any = {};
            for (const f of fs) {
                if (f.type == UserSearchFilterType.REFERENCE && f.default == 'current_year') {
                    const currentYear = new Date().getFullYear();
                    f.default = currentYear;
                }
                else if (f.type == UserSearchFilterType.REFERENCE && f.default == 'current_month') {
                    const currentMonth = new Date().getMonth();
                    f.default = currentMonth;
                }
                else {
                    let defaultValue = f.default;
                    let storedValue = state[f.name] ?? f.default;

                    if (f.type === UserSearchFilterType.MULTI_REFERENCE) {
                        if (typeof storedValue === 'string' && storedValue) {
                            storedValue = storedValue.split(',').map((v: string) => +v.trim()).filter((v: number) => !isNaN(v));
                        }
                        if (typeof defaultValue === 'string' && defaultValue) {
                            defaultValue = defaultValue.split(',').map((v: string) => +v.trim()).filter((v: number) => !isNaN(v));
                        }
                    }

                    defaultValues[f.name] = defaultValue;
                    values[f.name] = storedValue;
                }
            }

            if (!mountedRef.current) return;
            setFilterValues(values);
            setTemporaryFilterValues(values);
            setDefaultFilterValues(defaultValues);
            setFilters(fs);
            setTimeout(() => propagate.current = true, 700);

            buttonRef.current = null;
        }
        catch (_) {
            if (!mountedRef.current) return;
            setError(t('search.filters.notfound'));
        }
    });

    React.useEffect(() => {
        initialize();
    }, []);

    React.useEffect(() => {
        if (props.onChange && propagate.current) {
            props.onChange(filterValues);
        }

        if (props.persist) {
            const key = getLocalStorageKey();
            const storageValues: any = {};
            Object.keys(filterValues).forEach(key => {
                const value = filterValues[key];
                if (Array.isArray(value)) {
                    storageValues[key] = value.join(',');
                } else {
                    storageValues[key] = value;
                }
            });
            storageSet(key, storageValues);
        }
    }, [filterValues]);

    const applyFilters = () => {
        setShowPanel(false);

        let newFilterValues = { ...temporaryFilterValues };
        Object.keys(newFilterValues).forEach((key) => {
            if (newFilterValues[key] == 0 && typeof newFilterValues[key] !== 'boolean') {
                newFilterValues[key] = null;
            }
        })
        setFilterValues({ ...newFilterValues });
        setTemporaryFilterValues({ ...newFilterValues });
    };

    const doSearch = () => {
        let newFilterValues = { ...temporaryFilterValues };
        Object.keys(newFilterValues).forEach((key) => {
            if (newFilterValues[key] == 0 && typeof newFilterValues[key] !== 'boolean') {
                newFilterValues[key] = null;
            }
        })
        setFilterValues({ ...newFilterValues });
        setTemporaryFilterValues({ ...newFilterValues }); // Also update temporary values to stay in sync

        setTimeout(() => {
            setSearchOrder(true);
        }, 0);

        setShowPanel(false);
    }

    const renderFilterComponent = (f: IUserSearchFilter) => {
        if (f.type === UserSearchFilterType.BOOL) {
            return <InputSwitch
                checked={temporaryFilterValues[f.name] ?? filterValues[f.name] ?? false}
                onChange={e => {
                    if (props.awaitForApply || props.awaitForDoSearch) {
                        setTemporaryFilterValues({ ...temporaryFilterValues, [f.name]: e.value });
                    }
                    else {
                        setFilterValues((v: any) => ({ ...v, [f.name]: e.value }));
                    }
                }} />
        }
        else if (f.type === UserSearchFilterType.TEXT) {
            return <FilterInputTextOnEnter
                value={temporaryFilterValues[f.name] ?? filterValues[f.name] ?? ''}
                onChange={(v: string) => {
                    if (props.awaitForApply || props.awaitForDoSearch) {
                        setTemporaryFilterValues({ ...temporaryFilterValues, [f.name]: v });
                    }
                    else {
                        setFilterValues((values: any) => ({ ...values, [f.name]: v }));
                    }
                }
                } />
        }
        else if (f.type === UserSearchFilterType.TEXT_WITHOUT_ENTER) {
            return <FilterInputText
                value={temporaryFilterValues[f.name] ?? filterValues[f.name] ?? ''}
                onChange={(v: string) => {
                    if (props.awaitForApply || props.awaitForDoSearch) {
                        setTemporaryFilterValues({ ...temporaryFilterValues, [f.name]: v });
                    }
                    else {
                        setFilterValues((values: any) => ({ ...values, [f.name]: v }));
                    }
                }
                } />
        }
        else if (f.type === UserSearchFilterType.REFERENCE || f.type === UserSearchFilterType.MULTI_REFERENCE) {
            const referenceValues = props.references?.[f.name] ?? [];

            const values = () => {
                if (f.type === UserSearchFilterType.REFERENCE) {
                    if (temporaryFilterValues[f.name] != filterValues[f.name]) {
                        return temporaryFilterValues[f.name] ?? filterValues[f.name] ?? '';
                    }
                    else {
                        return filterValues[f.name] ?? '';
                    }
                }
                else {
                    if (temporaryFilterValues[f.name] != filterValues[f.name]) {
                        return temporaryFilterValues[f.name] ?? filterValues[f.name] ?? [];
                    }
                    else {
                        return filterValues[f.name] ?? [];
                    }
                }
            }

            const filterValue = values();

            if ((props.workId == 23 || props.workId == 22) && referenceValues.length == 0) {
                return null
            }

            if (f.type === UserSearchFilterType.MULTI_REFERENCE) {
                const options = referenceValues.map((r: any) => ({
                    label: f.shouldTranslate == false ? r.name : t(r.name),
                    value: +r.id
                }));

                return <MultiSelect
                    value={filterValue}
                    options={options}
                    onChange={(e: any) => {
                        if (props.awaitForApply || props.awaitForDoSearch) {
                            setTemporaryFilterValues({ ...temporaryFilterValues, [f.name]: e.value });
                        }
                        else {
                            setFilterValues((fv: any) => ({ ...fv, [f.name]: e.value }));
                        }
                    }}
                    placeholder={t('select.items') || 'Select items'}
                    className="w-full"
                    panelClassName='search-filters-dd-panel'
                />;
            }

            const options = referenceValues
                .sort((a: any, b: any) => typeof a.name == "number" && typeof b.name == "number" ? a.name - b.name :
                    f.shouldTranslate == false ? a.name?.localeCompare(b.name) : t(a.name).localeCompare(t(b.name))
                )
                .map((r: any) => ({
                    label: ((f.shouldTranslate == false ? r.name : t(r.name)) ?? '').substring(0, 50) + (((f.shouldTranslate == false ? r.name : t(r.name)) ?? '').length > 50 ? '...' : ''),
                    value: +r.id
                }));

            return <Select
                value={filterValue}
                options={options}
                onChange={(e: any) => {
                    if (props.awaitForApply || props.awaitForDoSearch) {
                        setTemporaryFilterValues({ ...temporaryFilterValues, [f.name]: e.value });
                    }
                    else {
                        setFilterValues((fv: any) => ({ ...fv, [f.name]: e.value }));
                    }
                }}
                placeholder=""
                className="w-full"
                panelClassName='search-filters-dd-panel'
                showClear={filterValues[f.name] != null}
                filter={true}
            />;
        }
        else if(f.type === UserSearchFilterType.SUB_REFERENCE) {

            const parentName  = f.name.replace(/Sub/ig, '').replace(/_+/g, '_');
            const parentValue = temporaryFilterValues[parentName];

            const referenceValues = props.references?.[parentName];

            if (!Array.isArray(referenceValues) || referenceValues.length == 0) {
                temporaryFilterValues[f.name] = null
                filterValues[f.name] = null;
                return null;
            }

            // The parent type only narrows the available options; it should not
            // block the sub-reference filter. With no parent selected, show every
            // subtype across all types.
            let references: any[] = [];
            if (parentValue == undefined || parentValue == null || parentValue == '' || parentValue == 0) {
                // The same subtype name is repeated across types with a different id
                // (e.g. "DUMPER" under both "MAQUINARIA CON MATRICULA" and "MAQUINARIA
                // SIN MATRICULA"). Deduplicate by name so the aggregated list isn't
                // cluttered with repeated options.
                const seen = new Set<string>();
                references = referenceValues.reduce((acc: any[], r: any) => {
                    (r.subTypes ?? []).forEach((s: any) => {
                        const key = (s.name ?? '').trim().toLowerCase();
                        if (!seen.has(key)) {
                            seen.add(key);
                            acc.push(s);
                        }
                    });
                    return acc;
                }, []);
                console.log('[SUB_REFERENCE] no parent', {
                    parentName,
                    referenceValues,
                    aggregated: references,
                    dedupNames: Array.from(seen),
                });
            }
            else {
                const parentMatch = referenceValues.find((r: any) => r.id == parentValue);
                references = parentMatch ? (parentMatch.subTypes ?? []) : [];
            }

            if (references == undefined || references.length == 0) {
                temporaryFilterValues[f.name] = null
                filterValues[f.name] = null;
                return null;
            }

            const values = () => {
                if (temporaryFilterValues[f.name] != filterValues[f.name]) {
                    return temporaryFilterValues[f.name] ?? filterValues[f.name] ?? '';
                }
                else {
                    return filterValues[f.name] ?? '';
                }
            }
            const filterValue = values();

            const options = references
                .sort((a: any, b: any) => typeof a.name == "number" && typeof b.name == "number" ? a.name - b.name :
                    f.shouldTranslate == false ? a.name?.localeCompare(b.name) : t(a.name).localeCompare(t(b.name))
                )
                .map((r: any) => ({
                    label: ((f.shouldTranslate == false ? r.name : t(r.name)) ?? '').substring(0, 50) + (((f.shouldTranslate == false ? r.name : t(r.name)) ?? '').length > 50 ? '...' : ''),
                    value: +r.id
                }));

            console.log('[SUB_REFERENCE] options', { parentName, parentValue, filterValue, options });

            // If the selected subtype no longer belongs to the current parent type
            // (e.g. the user changed the type after picking a subtype), clear it so the
            // dropdown doesn't show an empty selection.
            if (filterValue !== '' && filterValue != null && !options.some((o: any) => o.value == filterValue)) {
                filterValues[f.name] = null;
                temporaryFilterValues[f.name] = null;
            }

            return <Select
                value={filterValue}
                options={options}
                onChange={(e: any) => {
                    if (props.awaitForApply || props.awaitForDoSearch) {
                        setTemporaryFilterValues({ ...temporaryFilterValues, [f.name]: e.value });
                    }
                    else {
                        setFilterValues((fv: any) => ({ ...fv, [f.name]: e.value }));
                    }
                }}
                placeholder=""
                className="w-full"
                panelClassName='search-filters-dd-panel'
                showClear={filterValues[f.name] != null}
                filter={true}
            />;

        }
        else if(f.type === UserSearchFilterType.MULTI_SELECT) {
            const referenceValues = (props.references?.[f.name] ?? []).map((item : any) => ({
                label: (item.name ?? '').substring(0, 50) + ((item.name ?? '').length > 50 ? '...' : ''),
                value: item.id
            }));
            const values = () => {
                if (temporaryFilterValues[f.name] != filterValues[f.name]) {
                    return temporaryFilterValues[f.name] ?? filterValues[f.name] ?? [];
                }
                else {
                    return filterValues[f.name] ?? [];
                }
            }
            const filterValue = values();
            return <MultiSelect
                    value={filterValue}
                    options={referenceValues}
                    onChange={(e: any) => {
                        if (props.awaitForApply || props.awaitForDoSearch) {
                            setTemporaryFilterValues({ ...temporaryFilterValues, [f.name]: e.value });
                        }
                        else {
                            setFilterValues((v: any) => ({ ...v, [f.name]: e.value }));
                        }
                    }}

                    />
        }
        else if (f.type === UserSearchFilterType.DATE) {
            return <input
                type='date'
                value={temporaryFilterValues[f.name] ?? filterValues[f.name]}
                onChange={e => {
                    const value = e.target.value;
                    if (props.awaitForApply || props.awaitForDoSearch) {
                        setTemporaryFilterValues({ ...temporaryFilterValues, [f.name]: value });
                    }
                    else {
                        setFilterValues((v: any) => ({ ...v, [f.name]: value }))
                    }
                }} />
        }
        else if (f.type === UserSearchFilterType.YEAR) {
            const referenceValues = props.references?.[f.name] ?? [];

            if (filterValues[f.name] == undefined) {
                filterValues[f.name] = new Date().getFullYear();
            }

            const values = () => {
                if (temporaryFilterValues[f.name] != filterValues[f.name]) {
                    return temporaryFilterValues[f.name] ?? filterValues[f.name];
                }
                else {
                    return filterValues[f.name] ;
                }
            }

            const filterValue = values();
            const currentYear = new Date().getFullYear();

            const options = referenceValues
                .sort((a: any, b: any) =>  a.name - b.name
                )
                .map((r: any) => ({
                    label: ((f.shouldTranslate == false ? r.name : t(r.name)) ?? '').substring(0, 50) + (((f.shouldTranslate == false ? r.name : t(r.name)) ?? '').length > 50 ? '...' : ''),
                    value: +r.id
                }));

            return <Select
                value={filterValue}
                options={options}
                onChange={(e: any) => {
                    if (props.awaitForApply || props.awaitForDoSearch) {
                        setTemporaryFilterValues({ ...temporaryFilterValues, [f.name]: e.value });
                    }
                    else {
                        setFilterValues((fv: any) => ({ ...fv, [f.name]: e.value }));
                    }
                }}
                placeholder=""
                className="w-full"
                panelClassName='search-filters-dd-panel'
                showClear={filterValues[f.name] != null}
                filter={true}
                virtualScrollerOptions={{itemSize: 38}}
            />;
        }
        else if (f.type === UserSearchFilterType.MONTH) {
            const parentValue = temporaryFilterValues['year'];



            let referenceValues = props.references?.['month'] ?? [];
            const currentYear = new Date().getFullYear();
            if(parentValue == currentYear || parentValue == undefined) {
                const currentMonth = new Date().getMonth() + 1;
                referenceValues = referenceValues.filter((r: any) => r.id <= currentMonth);
            }

            const values = () => {
                if (temporaryFilterValues[f.name] != filterValues[f.name]) {
                    return temporaryFilterValues[f.name] ?? filterValues[f.name];
                }
                else {
                    return filterValues[f.name];
                }
            }

            const filterValue = values();
            return <select
                value={filterValue}
                onChange={e => {
                    const v = e.target.value;
                    if (props.awaitForApply || props.awaitForDoSearch) {
                        setTemporaryFilterValues({ ...temporaryFilterValues, [f.name]: +v });
                    }
                    else {
                        setFilterValues((fv: any) => ({ ...fv, [f.name]: +v }));
                    }
                    e.preventDefault();
                    e.stopPropagation();
                }}>
                <option></option>
                {referenceValues
                    .sort((a: any, b: any) =>  a.id - b.id
                    )
                    .map((r: any, i: number) => {
                        return <option key={i} value={+r.id}>{ r.name }</option>
                    }
                    )}
            </select>;
        }
        else if (f.type === UserSearchFilterType.DATE_RANGE) {
            return <div>
                <input
                    type='date'
                    className='flat-right'
                    value={temporaryFilterValues[f.name].length ?? filterValues[f.name]?.length ? temporaryFilterValues[f.name][0] ?? filterValues[f.name][0] : ''}
                    onChange={e => {
                        const value = e.target.value;
                        let oldValue = temporaryFilterValues[f.name] ?? filterValues[f.name];
                        if (oldValue == null) {
                            oldValue = [null, null];
                        }
                        oldValue[0] = value;
                        if (props.awaitForApply || props.awaitForDoSearch) {
                            setTemporaryFilterValues({ ...temporaryFilterValues, [f.name]: oldValue });
                        }
                        else {
                            setFilterValues((v: any) => ({ ...v, [f.name]: oldValue }))
                        }
                    }} />

                <input
                    type='date'
                    className='flat-left'
                    value={temporaryFilterValues[f.name].length ?? filterValues[f.name]?.length ? temporaryFilterValues[f.name][1] ?? filterValues[f.name][1] : ''}
                    onChange={e => {
                        const value = e.target.value;
                        let oldValue = temporaryFilterValues[f.name] ?? filterValues[f.name];
                        if (oldValue == null) {
                            oldValue = [null, null];
                        }
                        oldValue[1] = value;
                        if (props.awaitForApply || props.awaitForDoSearch) {
                            setTemporaryFilterValues({ ...temporaryFilterValues, [f.name]: oldValue });
                        }
                        else {
                            setFilterValues((v: any) => ({ ...v, [f.name]: oldValue }))
                        }
                    }} />
            </div>
        }
        else {
            return <strong>TODO</strong>
        }
    }

    const renderFilterRow = (f: IUserSearchFilter) => {

        const filterComponent = renderFilterComponent(f);
        if ((props.workId === 23 || props.workId == 22) && !filterComponent) { return null }

        return <div className='r' key={f.name}>
            <span className='info'>
                {f.help && <Button
                    icon='pi pi-info'
                    tooltip={f.help}
                    className='p-button-rounded  p-button-text p-button-sm' />}
            </span>
            <label>{t(f.title)}</label>
            {filterComponent}
        </div>
    }

    const clearFilters = (e: any) => {
        setShowPanel(false);
        panelRef.current.hide(e);
        setFilterValues(defaultFilterValues);
        setTemporaryFilterValues(defaultFilterValues);

        if (props.onChange) {
            props.onChange(defaultFilterValues);
        }
    }

    const closePanel = (e: any) => {
        panelRef.current.hide(e);
        setShowPanel(false);
    }

    const parentFilterName = (f: IUserSearchFilter) => {
        if (f.type === UserSearchFilterType.SUB_REFERENCE) {
            return f.name.replace(/Sub/ig, '').replace(/_+/g, '_');
        }
        return null;
    }

    const sortFilters = (fs: IUserSearchFilter[]) => {
        // Keep alphabetical order but always render a SUB_REFERENCE after its parent
        // (e.g. "machinery_sub_type" after "machinery_type") so the dependent dropdown
        // has its parent value available and displays correctly.
        const sortKey = (f: IUserSearchFilter) => {
            const parent = parentFilterName(f);
            return parent ? `${parent}~~zz~~${f.name}` : f.name;
        };
        return [...fs].sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    }

    const render = () => {
        return <div className='c search-filters'>
            {loading.renderBox()}
            {error === undefined &&
                <div className='filters-container'>
                    {sortFilters(filters).map?.(f => renderFilterRow(f))}
                </div>}
            {error !== undefined &&
                <div className='error'>
                    <i className='fas fa-exclamation-triangle' />
                    {error}
                </div>}

            <div className='footer'>
                <button onClick={closePanel} style={{ color: "black" }}>
                    {t('Close')}
                </button>
                <span className='e' />
                {props.awaitForApply &&
                    <button onClick={applyFilters} style={{ color: "black" }}>
                        {t('search.filters.apply')}
                    </button>}
                {props.awaitForDoSearch &&<button onClick={doSearch} style={{ color: "black" }}>
                        {t('search.filters.apply')}
                    </button>
                }
                <button onClick={clearFilters} style={{ color: "black" }}>
                    {t('search.filters.clear')}
                </button>
            </div>
        </div>
    };

    const [showPanel, setShowPanel] = React.useState<boolean>(false);

    const panelRef = React.useRef<any>();

    function RenderAsButton({ className }: { className?: string } = {}) {
        className = className || 'fas fa-filter pointer filter-btn';
        let hasFilters = false;

        for (const i in defaultFilterValues) {
            if (defaultFilterValues[i] != null && defaultFilterValues[i] !== undefined && defaultFilterValues[i] !== '') {
                hasFilters = true;
                break;
            }
        }

        if (!hasFilters) {
            for (const i in filterValues) {
                if (filterValues[i] != null && filterValues[i] !== undefined && filterValues[i] !== '') {
                    hasFilters = true;
                    break;
                }
            }
        }

        if (filters.length > 0) {
            return <div className='search-filters'>
                {props.getXml && <i title = {t('paginator.export')}
                    className={'pointer fas fa-file-excel filters-export'}
                    style={{ color: 'green' }}
                    onClick={(e) => {
                        props.getXml && props.getXml();
                    }} />}
                <i title={t('search.filter')}
                    className={className}
                    style={{ color: hasFilters ? "yellow" : undefined }}
                    onClick={(e) => {
                        if (props.awaitForApply || props.awaitForDoSearch) {
                            setShowPanel(a => !a)
                        } else {
                            panelRef.current.toggle(e);
                        }
                    }} />

                {showPanel && (props.awaitForApply || props.awaitForDoSearch) && <div className='p-overlaypanel p-component p-overlaypanel-enter-done overlay-panel p-overlaypanel-content'>
                    {render()}
                </div>}

                <OverlayPanel
                    className='no-padding'
                    ref={panelRef}>
                    {render()}
                </OverlayPanel>
            </div>;
        }
        else {
            return <strong></strong>;
        }
    }


    const renderAsButton = ({ className }: { className?: string } = {}) => {
        if (buttonRef.current == null) {
            const instance = <RenderAsButton className={className} />;
            buttonRef.current = instance;
        }

        React.useEffect(() => {
            return () => {
                buttonRef.current = null;
            }
        }, [filterValues]);

        return <RenderAsButton className={className} />
    }

    const Component = React.memo(
        RenderAsButton,
        (a: any, b: any) => {
            return true;
        });

    const setFilter = (name: string, value: any) => {
        setFilterValues((f: any) => ({ ...f, [name]: value }));
    }

    const setTemporaryFilter = (name: string, value: any) => {
        setTemporaryFilterValues((f: any) => ({ ...f, [name]: value }));
    }

    const hasFilters = () => {
        const keys = Object.keys(filterValues);
        const values = keys.map(k => filterValues[k]).filter(r => r !== undefined);
        return values.length > 0;
    }

    return {
        signature: JSON.stringify(filterValues),
        Component: Component,
        loading,
        filters: filterValues,
        temporaryFilterValues : temporaryFilterValues,
        searchOrder,
        returnSearchOrderToFalse,
        hasFilters,
        mergeFilters: (v: any) => ({ ...filterValues, ...v }),
        merge: (v: any) => ({ ...filterValues, ...v }),
        render: render,
        setFilter,
        setTemporaryFilter,
        renderAsButton: renderAsButton,
    }
}
